/**
 * Helpers for product mode — the mobile view of a cloud table.
 *
 * Each row becomes a sheet row (number, image, title, price) or a grid tile,
 * with one image chosen by column + position or picked per row from the
 * gallery. Everything here is pure so the hook and the components stay thin.
 */

import { expandImageUrls, isCurrencyValue } from "./renderUtils";
import { TITLE_HINTS } from "./galleryNavigation";
import {
  DEFAULT_PRODUCT_MODE_SETTINGS,
  PRODUCT_ANIMATION_MAX_SECONDS,
  PRODUCT_ANIMATION_MIN_SECONDS,
  PRODUCT_ANIMATION_VERSION,
  PRODUCT_GRID_COLUMN_OPTIONS,
  PRODUCT_MODE_LAYOUTS,
  PRODUCT_MODE_REVEAL_MAX_STEPS,
  PRODUCT_MODE_REVEAL_STAGGER_MS,
  PRODUCT_MODE_STORAGE_PREFIX,
  PRODUCT_MODE_VERSION,
} from "../constants";

const isVisibleColumn = (col, filters) => Boolean(col) && (Boolean(filters?.showDebugColumns) || !col.debug);

/**
 * Image columns a row's image can come from. Debug image columns stay hidden
 * unless the user revealed them, matching the table.
 */
export const getProductImageColumns = (columns, filters) =>
  (columns || []).filter((col) => col?.type === "image" && isVisibleColumn(col, filters));

/**
 * Columns that can be shown as a field on a row or tile (everything but images).
 */
export const getProductFieldColumns = (columns, filters) =>
  (columns || []).filter((col) => col?.type !== "image" && isVisibleColumn(col, filters));

/**
 * Every column the expanded record lists, images included, in table order.
 */
export const getProductRecordColumns = (columns, filters) => (columns || []).filter((col) => isVisibleColumn(col, filters));

const columnKey = (col) => String(col?.id || "").toLowerCase();
const columnLabel = (col) => String(col?.name || col?.id || "").trim().toLowerCase();
// "product_url" / "Product-URL" → "product url", so \b word matches work on ids too.
const words = (text) => text.replace(/[_\-.]+/g, " ");
const nonImage = (columns) => (columns || []).filter((col) => col && col.type !== "image");

const isHttpUrl = (value) => typeof value === "string" && /^https?:\/\//i.test(value.trim());

/** Hostname without "www." — what a row shows for a URL field. */
export const urlDomain = (url) => {
  if (typeof url !== "string" || !url.trim()) return "";
  try {
    return new URL(url.trim()).hostname.replace(/^www\./, "");
  } catch {
    return url.trim();
  }
};

const sampleRows = (rows, size = 25) => (rows || []).slice(0, size);

/**
 * The candidate whose filled sample values most often satisfy `test`, if it
 * clears the floor. Used when neither a type nor a name gives a column away.
 */
const bestByValues = (candidates, rows, test, minHits = 3, minRatio = 0.6) => {
  let best = null;
  for (const col of candidates) {
    let filled = 0;
    let hits = 0;
    for (const row of rows) {
      const value = row?.data?.[col.id];
      if (value === null || value === undefined || value === "") continue;
      filled += 1;
      if (test(value)) hits += 1;
    }
    if (hits >= minHits && hits / filled >= minRatio && (!best || hits > best.hits)) best = { col, hits };
  }
  return best?.col || null;
};

const PRICE_NAME = /price/i;
// A table often carries several price-like columns (compare-at, regular, min/max).
// The plain selling price is the one a product sheet should show.
const PRICE_SECONDARY = /compare|original|regular|old|was|min|max|range|currency|unit/i;

const pickPrimaryPrice = (candidates) =>
  candidates.find((col) => columnLabel(col) === "price") ||
  candidates.find((col) => !PRICE_SECONDARY.test(columnLabel(col)) && !PRICE_SECONDARY.test(columnKey(col))) ||
  candidates[0] ||
  null;

/**
 * Best guess at the table's price column: a column typed "price", else one named
 * like a price, else a column whose loaded values mostly look like currency.
 */
export const detectPriceColumn = (columns, rows = []) => {
  const candidates = nonImage(columns);
  if (!candidates.length) return null;

  const typed = candidates.filter((col) => col.type === "price" || col.type === "currency");
  if (typed.length) return pickPrimaryPrice(typed);

  const named = candidates.filter((col) => PRICE_NAME.test(columnKey(col)) || PRICE_NAME.test(columnLabel(col)));
  if (named.length) return pickPrimaryPrice(named);

  return bestByValues(candidates, sampleRows(rows), (value) => typeof value === "string" && isCurrencyValue(value));
};

const NAME_WORD = /\b(name|title|heading)\b/i;
const PRODUCT_WORD = /\b(product|item)\b/i;

/**
 * The column that names each row: one of the gallery's title hints, else a
 * "…name"/"…title" column (a product one first), else the first text column
 * whose loaded values are plain text.
 */
export const detectTitleColumn = (columns, rows = []) => {
  const candidates = nonImage(columns);
  if (!candidates.length) return null;

  const exact = candidates.find((col) => TITLE_HINTS.includes(columnKey(col)) || TITLE_HINTS.includes(columnLabel(col)));
  if (exact) return exact;

  const named = candidates.filter((col) => NAME_WORD.test(words(columnKey(col))) || NAME_WORD.test(words(columnLabel(col))));
  if (named.length) {
    return named.find((col) => PRODUCT_WORD.test(words(`${columnKey(col)} ${columnLabel(col)}`))) || named[0];
  }

  return bestByValues(
    candidates.filter((col) => !col.type || col.type === "text"),
    sampleRows(rows),
    (value) => typeof value === "string" && !isHttpUrl(value) && !isCurrencyValue(value),
    3,
    0.8
  );
};

const URL_WORD = /\b(url|link|href|permalink)\b/i;
const URL_EXCLUDE = /image|img|photo|thumb|video|logo|icon|avatar/i;

/**
 * The column holding each row's page on the source site: a "link" column, else
 * one named like a URL (a product one first), else a column whose loaded
 * values are mostly URLs. Image/media URL columns are skipped.
 */
export const detectUrlColumn = (columns, rows = []) => {
  const candidates = nonImage(columns).filter((col) => !URL_EXCLUDE.test(columnKey(col)) && !URL_EXCLUDE.test(columnLabel(col)));
  if (!candidates.length) return null;

  const urlNamed = (col) => URL_WORD.test(words(columnKey(col))) || URL_WORD.test(words(columnLabel(col)));
  const pickNamed = (list) =>
    list.find((col) => urlNamed(col) && PRODUCT_WORD.test(words(`${columnKey(col)} ${columnLabel(col)}`))) || list.find(urlNamed) || null;

  const typed = candidates.filter((col) => col.type === "link");
  if (typed.length) return pickNamed(typed) || typed[0];

  const named = pickNamed(candidates);
  if (named) return named;

  return bestByValues(candidates, sampleRows(rows), isHttpUrl);
};

/**
 * Every distinct image URL a row holds, in column order, optionally limited to
 * one column. A main image is usually repeated inside an "all images" column;
 * counting it twice would make "2nd image" show the same photo again.
 */
export const getRowImageCandidates = (row, imageColumns, imageColumnId = null) => {
  if (!row?.data) return [];
  const sources = imageColumnId ? (imageColumns || []).filter((col) => col.id === imageColumnId) : imageColumns || [];
  const seen = new Set();
  return sources
    .flatMap((col) => expandImageUrls(row.data[col.id]))
    .filter((url) => {
      if (seen.has(url)) return false;
      seen.add(url);
      return true;
    });
};

/**
 * Which image a row shows.
 *
 * A cover picked from the gallery wins as long as the row still holds that URL.
 * Otherwise the settings pick a position within the chosen source column(s),
 * clamped to the last image a row with fewer images has.
 *
 * @returns {{ url: string|null, index: number, candidates: string[], isCustom: boolean }}
 */
export const resolveTileImage = (row, settings, imageColumns) => {
  const all = getRowImageCandidates(row, imageColumns);
  const cover = row?.id != null ? settings?.covers?.[row.id] : null;
  if (cover && all.includes(cover)) {
    return { url: cover, index: all.indexOf(cover), candidates: all, isCustom: true };
  }

  const candidates = settings?.imageColumnId ? getRowImageCandidates(row, imageColumns, settings.imageColumnId) : all;
  if (!candidates.length) return { url: null, index: -1, candidates, isCustom: false };

  const wanted = Number.isInteger(settings?.imageIndex) ? settings.imageIndex : 0;
  const index = Math.min(Math.max(wanted, 0), candidates.length - 1);
  return { url: candidates[index], index, candidates, isCustom: false };
};

const EMPTY_VALUE = { text: "—", kind: "empty", href: null };

/**
 * Compact single-line text for a field, with a hint for styling. URLs come
 * back as their domain with the full link in `href`.
 *
 * @returns {{ text: string, kind: "price"|"link"|"text"|"empty", href: string|null }}
 */
export const formatFieldValue = (value, column) => {
  if (value === null || value === undefined || value === "") return EMPTY_VALUE;

  const isPriceColumn = column?.type === "price" || column?.type === "currency";

  if (typeof value === "number") {
    if (isPriceColumn) {
      return {
        text: `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        kind: "price",
        href: null,
      };
    }
    return { text: value.toLocaleString(), kind: "text", href: null };
  }
  if (typeof value === "boolean") return { text: value ? "Yes" : "No", kind: "text", href: null };
  if (typeof value === "object") {
    try {
      return { text: JSON.stringify(value), kind: "text", href: null };
    } catch {
      return { text: String(value), kind: "text", href: null };
    }
  }

  const text = String(value).replace(/\s+/g, " ").trim();
  if (!text) return EMPTY_VALUE;
  if (column?.type !== "image" && isHttpUrl(text)) {
    // A cell can hold several comma-separated URLs; the first one stands for the row.
    const href = text.split(/,\s*(?=https?:\/\/)/i)[0].trim();
    return { text: urlDomain(href), kind: "link", href };
  }
  return { text, kind: isPriceColumn || isCurrencyValue(text) ? "price" : "text", href: null };
};

/**
 * Where the sheet puts the chosen fields. The right-hand slot takes the price
 * (the detected price column, else any price-typed field, else the last
 * field); the first remaining field is the title line and a second one sits
 * under it. A single field is just the title line.
 */
export const placeSheetFields = (fields = [], priceColumnId = null) => {
  if (!fields.length) return { primary: null, secondary: null, trailing: null };
  let trailing = null;
  if (fields.length >= 2) {
    trailing =
      (priceColumnId != null && fields.find((col) => col.id === priceColumnId)) ||
      fields.find((col) => col.type === "price" || col.type === "currency") ||
      fields[fields.length - 1];
  }
  const rest = fields.filter((col) => col !== trailing);
  return { primary: rest[0] || null, secondary: rest[1] || null, trailing };
};

/** Inline style that staggers the reveal animation by row/tile position. */
export const revealDelayStyle = (index) => ({
  animationDelay: `${Math.min(Math.max(index, 0), PRODUCT_MODE_REVEAL_MAX_STEPS) * PRODUCT_MODE_REVEAL_STAGGER_MS}ms`,
});

const ORDINALS = ["1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th"];

/** "1st", "2nd", … for an image position (0-based). */
export const ordinalLabel = (index) => ORDINALS[index] || `${index + 1}th`;

const storageKey = (tableId) => `${PRODUCT_MODE_STORAGE_PREFIX}${tableId}`;

/**
 * Coerce stored (or stale) settings into a valid shape, falling back per field.
 */
export const sanitizeProductModeSettings = (settings) => {
  const raw = settings && typeof settings === "object" ? settings : {};
  const fieldIds = Array.isArray(raw.fieldIds)
    ? raw.fieldIds.filter((id) => typeof id === "string" || typeof id === "number")
    : null;
  return {
    ...DEFAULT_PRODUCT_MODE_SETTINGS,
    enabled: Boolean(raw.enabled),
    layout: PRODUCT_MODE_LAYOUTS.includes(raw.layout) ? raw.layout : DEFAULT_PRODUCT_MODE_SETTINGS.layout,
    imageColumnId: typeof raw.imageColumnId === "string" || typeof raw.imageColumnId === "number" ? raw.imageColumnId : null,
    imageIndex: Number.isInteger(raw.imageIndex) && raw.imageIndex >= 0 ? raw.imageIndex : 0,
    imageFit: raw.imageFit === "contain" ? "contain" : "cover",
    gridColumns: PRODUCT_GRID_COLUMN_OPTIONS.includes(raw.gridColumns) ? raw.gridColumns : DEFAULT_PRODUCT_MODE_SETTINGS.gridColumns,
    fieldIds,
    hideWithoutImages: typeof raw.hideWithoutImages === "boolean" ? raw.hideWithoutImages : DEFAULT_PRODUCT_MODE_SETTINGS.hideWithoutImages,
    covers: raw.covers && typeof raw.covers === "object" && !Array.isArray(raw.covers) ? raw.covers : {},
  };
};

export const loadProductModeSettings = (tableId) => {
  if (!tableId) return { ...DEFAULT_PRODUCT_MODE_SETTINGS };
  try {
    const raw = localStorage.getItem(storageKey(tableId));
    if (!raw) return { ...DEFAULT_PRODUCT_MODE_SETTINGS };
    const parsed = JSON.parse(raw);
    if (parsed?.version !== PRODUCT_MODE_VERSION) return { ...DEFAULT_PRODUCT_MODE_SETTINGS };
    return sanitizeProductModeSettings(parsed.settings);
  } catch (err) {
    console.error("Error loading product mode settings:", err);
    return { ...DEFAULT_PRODUCT_MODE_SETTINGS };
  }
};

export const saveProductModeSettings = (tableId, settings) => {
  if (!tableId) return;
  try {
    localStorage.setItem(storageKey(tableId), JSON.stringify({ version: PRODUCT_MODE_VERSION, settings }));
  } catch (err) {
    console.error("Error saving product mode settings:", err);
  }
};

/** Clamp an animation duration to the supported range, in whole seconds. */
export const clampAnimationSeconds = (value, fallback) => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds)) return fallback;
  return Math.min(PRODUCT_ANIMATION_MAX_SECONDS, Math.max(PRODUCT_ANIMATION_MIN_SECONDS, Math.round(seconds)));
};

/** On/off plus a duration, the shape every full-screen animation setting has. */
export const sanitizeAnimationSettings = (settings, defaults) => {
  const raw = settings && typeof settings === "object" ? settings : {};
  return {
    enabled: Boolean(raw.enabled),
    durationSec: clampAnimationSeconds(raw.durationSec, defaults.durationSec),
  };
};

// Animation settings are per device, not per table: the same recording setup
// should carry over to the next extraction's table.
export const loadAnimationSettings = (storageKey, defaults) => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return { ...defaults };
    const parsed = JSON.parse(raw);
    if (parsed?.version !== PRODUCT_ANIMATION_VERSION) return { ...defaults };
    return sanitizeAnimationSettings(parsed.settings, defaults);
  } catch (err) {
    console.error("Error loading animation settings:", err);
    return { ...defaults };
  }
};

export const saveAnimationSettings = (storageKey, settings) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify({ version: PRODUCT_ANIMATION_VERSION, settings }));
  } catch (err) {
    console.error("Error saving animation settings:", err);
  }
};
